import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ApiError, Departamento, Dispositivo, Empregador } from '../../api.service';
import { RodapeCrudComponent } from '../../shared/rodape-crud.component';
import { ServicoMensagemAplicacao } from '../../shared/servico-mensagem-aplicacao';
import { GradeTerminaisRelogioComponent } from './grade-terminais-relogio.component';
import { PainelDadosTerminalComponent } from './painel-dados-terminal.component';
import { ServicoTerminalRelogio } from './servico-terminal-relogio';

@Component({
  selector: 'app-painel-terminais-relogio',
  standalone: true,
  imports: [CommonModule, GradeTerminaisRelogioComponent, PainelDadosTerminalComponent, RodapeCrudComponent],
  templateUrl: './painel-terminais-relogio.component.html'
})
export class PainelTerminaisRelogioComponent {
  @Input() empregadores: Empregador[] = [];
  @Input() departamentos: Departamento[] = [];
  @Input() empregadorSelecionado = '';
  @Input({ required: true }) terminal!: Dispositivo;
  @Input() terminais: Dispositivo[] = [];

  @Output() empregadorSelecionadoChange = new EventEmitter<string>();
  @Output() alterarEmpregador = new EventEmitter<void>();
  @Output() mensagem = new EventEmitter<string>();
  @Output() terminalChange = new EventEmitter<Dispositivo>();
  @Output() terminalSalvo = new EventEmitter<Dispositivo>();
  @Output() terminalLimpo = new EventEmitter<Dispositivo>();
  @Output() terminalRevogado = new EventEmitter<Dispositivo>();
  @Output() terminalExcluido = new EventEmitter<void>();

  importando = false;
  ligandoFoto = false;

  constructor(
    private readonly servicoMensagemAplicacao: ServicoMensagemAplicacao,
    private readonly servicoTerminalRelogio: ServicoTerminalRelogio
  ) {}

  selecionarEmpregador(empregadorId: string) {
    this.empregadorSelecionadoChange.emit(empregadorId);
    this.alterarEmpregador.emit();
  }

  limparTerminal() {
    this.terminal = this.servicoTerminalRelogio.novoTerminal(this.empregadorSelecionado);
    this.terminalChange.emit(this.terminal);
    this.terminalLimpo.emit(this.terminal);
  }

  selecionarTerminal(terminal: Dispositivo) {
    this.terminal = this.servicoTerminalRelogio.prepararEdicao(terminal, this.empregadorSelecionado);
    this.terminalChange.emit(this.terminal);
    this.terminalLimpo.emit(this.terminal);
  }

  alterarTerminal(terminal: Dispositivo) {
    this.terminal = terminal;
    this.terminalChange.emit(terminal);
  }

  salvarTerminal() {
    if (!this.empregadorSelecionado) {
      this.mensagem.emit(this.servicoTerminalRelogio.mensagemEmpregadorObrigatorioTerminal());
      return;
    }

    const validacao = this.servicoTerminalRelogio.validarTerminal(this.terminal);
    if (validacao) {
      this.mensagem.emit(validacao);
      return;
    }

    const terminalParaSalvar = this.servicoTerminalRelogio.prepararEnvio(this.terminal);
    const editando = !!terminalParaSalvar.id;
    this.servicoTerminalRelogio.salvar(terminalParaSalvar, this.empregadorSelecionado).subscribe({
      next: salvo => {
        this.mensagem.emit(this.servicoMensagemAplicacao.registroSalvo(editando));
        this.terminal = this.servicoTerminalRelogio.novoTerminal(this.empregadorSelecionado);
        this.terminalSalvo.emit(salvo);
      },
      error: (erro: ApiError) => this.mensagem.emit(this.servicoTerminalRelogio.mensagemErroSalvarTerminal(erro))
    });
  }

  revogarTerminalAtivacao(terminal: Dispositivo) {
    if (!terminal.id || !window.confirm(this.servicoTerminalRelogio.mensagemConfirmarRevogarAtivacao(terminal))) {
      return;
    }

    this.servicoTerminalRelogio.revogarAtivacao(terminal).subscribe({
      next: revogado => {
        this.mensagem.emit(this.servicoTerminalRelogio.mensagemAtivacaoRevogada());
        this.terminalRevogado.emit(revogado);
      },
      error: (erro: ApiError) => this.mensagem.emit(this.servicoTerminalRelogio.mensagemErroRevogarAtivacao(erro))
    });
  }

  excluirTerminal() {
    if (!this.terminal.id || !window.confirm(this.servicoTerminalRelogio.mensagemConfirmarExclusao(this.terminal))) {
      return;
    }

    this.servicoTerminalRelogio.excluir(this.terminal).subscribe({
      next: () => {
        this.mensagem.emit(this.servicoMensagemAplicacao.registroExcluido());
        this.terminal = this.servicoTerminalRelogio.novoTerminal(this.empregadorSelecionado);
        this.terminalExcluido.emit();
      },
      error: (erro: ApiError) => this.mensagem.emit(this.servicoTerminalRelogio.mensagemErroExcluirTerminal(erro))
    });
  }

  importarServidoresControlId() {
    const validacao = this.servicoTerminalRelogio.validarImportacaoControlId(this.terminal);
    if (validacao) {
      this.mensagem.emit(validacao);
      return;
    }
    this.importando = true;
    this.servicoTerminalRelogio.importarControlId(this.terminal).subscribe({
      next: resultado => {
        this.importando = false;
        this.terminal = { ...this.terminal, senhaLeitor: '', temSenhaLeitor: true };
        this.terminalChange.emit(this.terminal);
        this.mensagem.emit(this.servicoTerminalRelogio.mensagemResultadoImportacao(resultado));
      },
      error: (erro: ApiError) => {
        this.importando = false;
        this.mensagem.emit(this.servicoTerminalRelogio.mensagemErroImportacao(erro));
      }
    });
  }

  ligarFotoMonitorControlId() {
    const validacao = this.servicoTerminalRelogio.validarImportacaoControlId(this.terminal);
    if (validacao) {
      this.mensagem.emit(validacao);
      return;
    }
    this.ligandoFoto = true;
    this.servicoTerminalRelogio.habilitarFotoMonitor(this.terminal).subscribe({
      next: () => {
        this.ligandoFoto = false;
        this.terminal = { ...this.terminal, senhaLeitor: '', temSenhaLeitor: true };
        this.terminalChange.emit(this.terminal);
        this.mensagem.emit(this.servicoTerminalRelogio.mensagemFotoMonitorLigada());
      },
      error: (erro: ApiError) => {
        this.ligandoFoto = false;
        this.mensagem.emit(this.servicoTerminalRelogio.mensagemErroFotoMonitor(erro));
      }
    });
  }
}
